import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ServiceFAQ } from '@/components/sections/ServiceFAQ';
import { useJsonLd } from '@/hooks/useJsonLd';

const irsFAQs = [
  { q: "What should I do if I receive an IRS notice in North Carolina?", a: "Don't ignore it and don't panic. IRS notices have deadlines — typically 30 to 60 days to respond. Bring it to a CPA who can interpret the notice, verify whether the IRS's claim is accurate, and respond on your behalf. KGOB's IRS representation team handles this from our Charlotte, Winston-Salem, and Mooresville offices, as well as remotely for clients across the United States." },
  { q: "Can a CPA represent me before the IRS?", a: "Yes. Licensed CPAs, enrolled agents, and tax attorneys are authorized to represent taxpayers before the IRS under IRS Circular 230. KGOB files Form 2848 (Power of Attorney) to handle all communication with the IRS on your behalf — you never have to speak to them directly." },
  { q: "What is an Offer in Compromise and do I qualify?", a: "An Offer in Compromise (OIC) allows you to settle your tax debt for less than the full amount owed. The IRS considers your income, expenses, asset equity, and ability to pay. Qualification is strict — the IRS accepts less than 40% of OIC applications. KGOB evaluates your eligibility before you apply, so you don't waste the $205 application fee on a long-shot submission." },
  { q: "How long does IRS tax resolution take?", a: "It depends on the complexity. A simple penalty abatement can be resolved in 30-60 days. Installment agreements typically take 30-90 days. Offers in Compromise can take 6-12 months. Audit representation varies by scope. KGOB provides a realistic timeline after reviewing your specific situation." },
  { q: "What happens if I have unfiled tax returns?", a: "The IRS can file a Substitute for Return (SFR) on your behalf — and they won't give you any deductions or credits you're entitled to. Filing your own returns, even late, almost always results in a lower balance. KGOB helps clients file delinquent returns, apply for penalty abatement, and set up payment plans for any remaining balance." },
  { q: "Does KGOB handle state tax issues as well as IRS?", a: "Yes. We represent clients before the IRS and state tax agencies including the North Carolina Department of Revenue. State tax issues — back taxes, sales tax audits, withholding disputes — are handled with the same level of expertise and representation." }
];

const IRSRelief = () => {
    useJsonLd('irs-relief-0', {"@context": "https://schema.org", "@type": "Service", "name": "IRS Representation & Tax Relief", "description": "Licensed CPAs representing you before the IRS. Notice response, penalty abatement, installment agreements, Offers in Compromise, audit defense, lien removal, and Currently Not Collectible status.", "url": "https://kgob.com/services/irs-relief", "provider": {"@type": "AccountingService", "name": "KGOB CPAs & Advisors", "url": "https://kgob.com", "address": {"@type": "PostalAddress", "addressLocality": "Charlotte", "addressRegion": "NC", "addressCountry": "US"}}, "serviceType": ["IRS Representation", "Tax Relief", "Penalty Abatement", "Offer in Compromise", "IRS Audit Defense", "Tax Lien Removal", "Installment Agreement"], "areaServed": {"@type": "Country", "name": "United States"}});

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>IRS Representation & Tax Relief Services | Notices, Audits, Resolution | KGOB</title>
        <meta name="description" content="Tax relief services and IRS representation for notices, audits, and resolution. Calm guidance when IRS or state issues demand a clear response." />
        <link rel="canonical" href="https://kgob.com/services/irs-relief" />
        <meta property="og:title" content="IRS Representation & Relief | KGOB CPAs & Advisors" />
        <meta property="og:description" content="IRS notice response, penalty abatement, payment plans, Offers in Compromise, and audit defense. Licensed CPAs representing you before the IRS." />
        <meta property="og:url" content="https://kgob.com/services/irs-relief" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="flex justify-center mb-6">
                <Shield className="w-16 h-16 text-[#D4AF37]" />
              </div>
              
              <h1 className="font-['Playfair_Display'] text-5xl font-bold text-[#F7F6F2] mb-6">
                IRS Representation & Tax Relief
              </h1>
              
              <p className="text-xl text-[#D4AF37] italic mb-4">
                Calm, clarity, and representation when it matters most.
              </p>
              
              <p className="text-lg text-[#F7F6F2]/90 mb-6">
                KGOB provides tax relief services and IRS representation when you face IRS or state notices. We interpret what the notice means, respond on your behalf, and guide next steps so you are not decoding it alone.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Button data-testid="i-r-s-relief-button-1" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <Link data-testid="i-r-s-relief-link-4" to="/book-consultation?source=services-irs-relief">Get IRS Help Now</Link>
                </Button>
                <Button data-testid="i-r-s-relief-button-3" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                  <Link data-testid="i-r-s-relief-link-3" to="/services">View All Services</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Keyword focus */}
        <section className="py-16 bg-white" data-testid="irs-keyword-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-[#0B2240] mb-4">
              Tax relief and IRS representation
            </h2>
            <p className="text-lg text-[#4C5A70] max-w-3xl mx-auto">
              From first notice to final resolution, licensed CPAs handle audits, penalty abatement, and payment plans on your behalf.
            </p>
          </div>
        </section>

        {/* Is This Right for You? */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Is This Right for You?
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7]">
              <p className="text-[#4C5A70] mb-6">
                This service is designed for individuals and business owners dealing with IRS or state tax issues. You will benefit most if:
              </p>
              
              <div className="space-y-3">
                {[
                  'You received an IRS or state tax notice and need help understanding it',
                  'You have unfiled returns from prior years',
                  'You owe back taxes and need payment plan assistance',
                  'You are facing an audit or examination',
                  'You want professional representation to handle all IRS communication'
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4C5A70]">{item}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* What You Get */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What You Get
            </h2>
            
            <div className="space-y-8">
              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Notice Interpretation & Response</h3>
                <p className="text-[#4C5A70] mb-4">
                  We review your IRS or state notice, explain what it means in plain language, and respond on your behalf.
                </p>
                <ul className="space-y-2">
                  {[
                    'CP2000 notices (unreported income)',
                    'Balance due notices',
                    'Audit and examination letters',
                    'Collection notices and Intent to Levy'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Transcript Retrieval & Analysis</h3>
                <p className="text-[#4C5A70] mb-4">
                  We obtain your IRS account transcripts, review your filing history, and identify any missing returns or outstanding balances.
                </p>
                <ul className="space-y-2">
                  {[
                    'Account transcript retrieval',
                    'Wage and income transcript review',
                    'Filing compliance check',
                    'Plain-language summary of your IRS account status'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Payment Plans & Penalty Relief</h3>
                <p className="text-[#4C5A70] mb-4">
                  If you owe back taxes, we help you establish payment plans, request penalty abatement, and explore other relief options.
                </p>
                <ul className="space-y-2">
                  {[
                    'Installment agreement setup',
                    'Penalty abatement requests (first-time, reasonable cause)',
                    'Offer in Compromise evaluation',
                    'Currently Not Collectible status assistance'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              How It Works
            </h2>
            
            <div className="space-y-6">
              {[
                { step: '1', title: 'Initial Consultation', desc: 'We review your notice, gather background information, and explain your situation in plain language.' },
                { step: '2', title: 'Transcript & Records Review', desc: 'We obtain your IRS account transcripts and review your filing history to understand the full picture.' },
                { step: '3', title: 'Response Strategy', desc: 'We develop a response plan and represent you in all communications with the IRS or state agency.' },
                { step: '4', title: 'Resolution & Follow-Up', desc: 'We work to resolve the issue, set up payment plans if needed, and monitor your account for compliance.' },
                { step: '5', title: 'Ongoing Monitoring', desc: 'After resolution, we can monitor your IRS account to ensure no new issues arise.' }
              ].map((item, i) => (
                <Card key={i} className="p-6 border-2 border-[#E1DFD7]">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-lg">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-2">{item.title}</h3>
                      <p className="text-[#4C5A70]">{item.desc}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Changes */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What Changes
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Functional</h3>
                <p className="text-[#4C5A70] text-sm">
                  Your IRS issues are resolved or managed with a clear path forward, and all communication is handled by professionals.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Emotional</h3>
                <p className="text-[#4C5A70] text-sm">
                  You feel relief knowing a CPA is managing your IRS situation and you no longer have to face it alone.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Strategic</h3>
                <p className="text-[#4C5A70] text-sm">
                  With IRS issues behind you, you can focus on your business and financial goals without distraction.
                </p>
              </Card>
            </div>
          </div>
        </section>


        <ServiceFAQ faqs={irsFAQs} />

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] to-[#0f2d4f]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-[#F7F6F2] mb-6">
              Need Help with an IRS Notice?
            </h2>
            <p className="text-lg text-[#F7F6F2]/90 mb-10">
              Do not face the IRS alone. Schedule a consultation and let us handle the communication on your behalf.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button data-testid="i-r-s-relief-button-2" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="i-r-s-relief-link-2" to="/book-consultation?source=services-irs-relief">Get IRS Help Now</Link>
              </Button>
              <Button data-testid="i-r-s-relief-button-1" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                <Link data-testid="i-r-s-relief-link-1" to="/services">View All Services</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default IRSRelief;